import { useState } from 'react';
import { istanbulDayString, t } from '@aquabox/shared';
import { PrintPdfButton } from './PrintPdfButton';

/**
 * Aralığın gün üst sınırı — sunucudaki `customers.schema.ts#MAX_STATEMENT_DAYS` ile AYNI olmalı.
 *
 * KOPYA BİLEREK DURUYOR (Reports.tsx#MAX_EXPORT_DAYS ile aynı gerekçe): panel `@aquabox/api`'yi
 * import edemez, ortak sabit modülü yok. Panel karar VERMEZ, yalnız gönderilmiyecek bir isteği
 * göndermez; son söz sunucudur (aşan aralıkta 400 döner).
 */
const MAX_STATEMENT_DAYS = 366;

/** Aralığın gün sayısı, iki uç DAHİL. Boş ya da ters aralıkta `null`. */
function rangeDays(from: string, to: string): number | null {
  if (
    from === '' ||
    to === '' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(from) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(to)
  )
    return null;
  const ms = Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`);
  if (!Number.isFinite(ms)) return null;
  const days = Math.round(ms / 86_400_000) + 1;
  return days < 1 ? null : days;
}

/**
 * Müşteri cari ekstresi penceresi (O2, §3.13.2).
 *
 * Dönem İstanbul günleriyle seçilir ve **sunucuya hiçbir para hesabı gitmez** — panel yalnız
 * `from`/`to` gönderir, açılış/kapanış bakiyesi ve satırlar PDF'i üreten sunucuda defterden
 * SUM'lanır (kural 1 ve 4).
 *
 * Varsayılan dönem: ayın ilk günü → bugün. `window.open` tıklama anında SENKRON açılması
 * gerektiği için PDF indirmeyi `PrintPdfButton` yapar (bkz. PrintPdfButton yorumu); panel
 * `fetch` + `window.open` deseni pop-up engeline takılırdı.
 */
export function StatementDialog({
  customerId,
  customerName,
  onClose,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
}) {
  // Kullanıcının gördüğü gün İstanbul günüdür (kural 8); UTC günü değil.
  const today = istanbulDayString(new Date());
  const [from, setFrom] = useState(() => `${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(() => today);

  const days = rangeDays(from, to);
  const rangeOk = days !== null && days <= MAX_STATEMENT_DAYS;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('cash.statementDialog')}
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card modal-card" style={{ '--modal-w': '400px' } as React.CSSProperties}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
          {t('cash.statementFor', { customer: customerName })}
        </h3>

        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {t('cash.statementFrom')}
            </span>
            <input
              type="date"
              className="input"
              aria-label={t('cash.statementFrom')}
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              style={{ height: 38, fontSize: 13 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {t('cash.statementTo')}
            </span>
            <input
              type="date"
              className="input"
              aria-label={t('cash.statementTo')}
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              style={{ height: 38, fontSize: 13 }}
            />
          </label>
        </div>

        <p
          style={{
            fontSize: 12,
            color: rangeOk ? 'var(--text-secondary)' : 'var(--danger)',
            margin: '12px 0 0',
          }}
        >
          {rangeOk
            ? t('cash.statementDateHint', { n: MAX_STATEMENT_DAYS })
            : t('cash.statementInvalid', { n: MAX_STATEMENT_DAYS })}
        </p>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <PrintPdfButton
            path={`/api/customers/${customerId}/statement.pdf?from=${from}&to=${to}`}
            label={t('cash.statementOpen')}
            disabled={!rangeOk}
            onOpened={onClose}
          />
          <button className="btn" onClick={onClose} style={{ flex: 1 }}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
